/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ChartType } from "~/generated/graphql";
import { DateRange, EstatePeriod, Granularity, Period } from "~/components";
import { EstateGroupBy, APPLICATIONS_SORT_OPTIONS } from "./components";
import {
  getEndDate,
  getStartDate,
  useEstateRecordsCostPerApplication,
  useEstateRecordsGroupByChartJob,
  EstateTotals,
  EstateRecordsGroupingChart,
  EstateRecordsGroupingTable,
  TotalCostTable,
} from "./components/Charts";
import { mapEstatePeriodToPeriod } from "./helpers";

type Props = {
  period: EstatePeriod;
  range: DateRange | undefined;
  granularity: Granularity;
};

export function EstateRecordsApplicationsView({
  period,
  range,
  granularity,
}: Props) {
  const startDate = getStartDate(period, range);
  const endDate = getEndDate(period, range);

  const {
    isSubmitLoading,
    isChartLoading,
    chartData,
    isTableLoading,
    tableData,
    tableError,
    sortField,
    sortDirection,
    onSortSelect,
    onSortOrderChange,
  } = useEstateRecordsGroupByChartJob({
    chartType: ChartType.Application,
    startDate,
    endDate,
  });

  const mappedPeriod = mapEstatePeriodToPeriod(period);

  const {
    costPerApplicationChartData,
    costPerApplicationTableData,
    tableCount,
    extraDetails,
    allColumns,
    stackBarColors,
    chartKeys,
  } = useEstateRecordsCostPerApplication({
    chartData,
    tableData,
    granularity,
    period: mappedPeriod,
    startDate,
    endDate,
  });

  const firstColumnWidth = mappedPeriod === Period.MONTH ? "30%" : undefined;

  return (
    <div>
      <EstateRecordsGroupingChart
        data={costPerApplicationChartData}
        granularity={granularity}
        loading={isSubmitLoading || isChartLoading}
        startDate={startDate}
        endDate={endDate}
        period={period}
        colors={stackBarColors}
        keys={chartKeys}
      />
      <EstateTotals period={period} range={range} chartType={EstateGroupBy.APP}>
        <TotalCostTable
          count={tableData?.chartJobTable?.data?.count}
          isLoading={isSubmitLoading || isTableLoading}
          chartType={EstateGroupBy.APP}
          totalTop={extraDetails?.totalTop ?? "-"}
          totalOther={extraDetails?.totalOther ?? "-"}
        />
      </EstateTotals>
      <EstateRecordsGroupingTable
        loading={isSubmitLoading || isTableLoading}
        sortField={sortField}
        sortDirection={sortDirection}
        sortOptions={APPLICATIONS_SORT_OPTIONS}
        tableCount={tableCount ?? 0}
        columns={allColumns}
        data={costPerApplicationTableData}
        firstColumnWidth={firstColumnWidth}
        error={tableError}
        period={period}
        onSortSelect={onSortSelect}
        onSortDirectionSelect={onSortOrderChange}
      />
    </div>
  );
}
